let jwt_decode = require("jwt-decode");
import * as moment from "moment";
import {Injectable} from "@angular/core";
import {stringFormat} from "../Helpers/StringFormat";
import {Names} from "../names";
import {UserService} from "./UserService";
import {GlobalService} from "../GlobalService";
import {Location as LocationService} from "@angular/common";
import {ActivatedRoute} from "@angular/router";
import {AsyncSubject} from "rxjs/AsyncSubject";
import {AppSettings} from "../AppSettings";
import {LocalStorageService} from "../Storage/LocalStorageService";
import {Observable} from "rxjs/Observable";
import {resolveCurrentTenant, DEFAULT_TENANT} from "../Branding/TenantResolver";
import "rxjs/add/observable/interval";
import "rxjs/add/operator/takeUntil";

const ID_TOKEN_KEY: string = "id_token";
const SCOPE_OPEN_ID: string = "openid";

@Injectable()
export class AuthorizationService implements NPS.Common.Authorization.IAuthorizationService {
    systemConfiguration: NPS.Common.Branding.ISystemConfiguration;
    expiredRenewalTimeout: number;

    constructor(
        private activatedRoute: ActivatedRoute,
        private locationService: LocationService,
        private userService: UserService,
        private globalService: GlobalService,
        private localStorageService: LocalStorageService
        ) {
        this.systemConfiguration = AppSettings.systemConfiguration;
        this.systemConfiguration.SsoRedirectUri = this.getRelativeSsoRedirectUri();
        if (this.systemConfiguration.MultiTenancyEnabled) {
            const tenant = resolveCurrentTenant(this.globalService.location.host);
            const curredTenant = tenant in this.systemConfiguration.TenantSsoUrls ? tenant : DEFAULT_TENANT;
            this.systemConfiguration.SsoAuthorizationServer = this.systemConfiguration.TenantSsoUrls[curredTenant];
        }
    }

    private getRelativeSsoRedirectUri(): string {
        return this.globalService.location.origin + "/#/oauthcallback/";
    }

    private getLogoutSsoRedirectUri(): string {
        return this.globalService.location.origin;
    }



    getAuthorizeUrl(): string {
        var state = encodeURIComponent(this.storeNewState());
        var redirectUri = this.systemConfiguration.SsoRedirectUri.replace(/#/g, "%23");
        let isSystemConfigOpenId = this.systemConfiguration.SsoScopes.includes(SCOPE_OPEN_ID);

        return stringFormat("{baseUrl}{path}?{clientIdParam}={clientId}&" +
            "{redirectUriParam}={redirectUri}&{responseTypeParam}={responseType}&{scopeParam}={scope}&{stateParam}={state}&" +
            "{nonceParam}={nonce}", {
                baseUrl: this.systemConfiguration.SsoAuthorizationServer,
                path: Names.authorization.ssoAuthorizePath,
                clientIdParam: Names.authorization.clientIdParam,
                clientId: this.systemConfiguration.SsoClientId,
                redirectUriParam: Names.authorization.redirectUriParam,
                redirectUri: redirectUri,
                responseTypeParam: Names.authorization.responseTypeParam,
                responseType: isSystemConfigOpenId ? Names.authorization.responseType : Names.authorization.responseTypeNoOpenId,
                scopeParam: Names.authorization.scopeParam,
                scope: this.systemConfiguration.SsoScopes,
                stateParam: Names.authorization.stateParam,
                state: state,
                nonceParam: Names.authorization.nonceParam,
                nonce: state
            });
    }

    trySilentLogin(): Promise<string> {
        const source = new AsyncSubject();
        const iframe = $("<iframe id=\"silentLoginFrame\" name=\"silentLoginFrame\"></iframe>")
            .attr("src", this.getAuthorizeUrl() + "&prompt=none")
            .attr("width", 0)
            .attr("height", 0)
            .css("visibility", "hidden");
        const self = this;

        iframe.on("load", function () {
            const authResponse: NPS.Common.Authorization.ISSOAuthResponse = self.parseParams((<HTMLIFrameElement> this).contentWindow.location.toString());
            this.parentNode.removeChild(this);
            self.verifyStateParameters(authResponse.state);
            if (authResponse.id_token) {
                self.localStorageService.set(ID_TOKEN_KEY, authResponse.id_token);
            }
            if (authResponse.access_token) {
                self.saveUserInfo(authResponse.access_token);
                source.next(authResponse.access_token);
                source.complete();
            } else {
                source.next(authResponse.error);
                source.error(authResponse.error);
            }
        });

        $("body").append(iframe);

        return source.toPromise() as Promise<string>;
    }

    requestSSOLogin(): void {
        this.userService.clearUser();
        this.globalService.window.location.replace(this.getAuthorizeUrl());
    }

    logout() {
        let idToken = this.localStorageService.get(ID_TOKEN_KEY);
        this.userService.clearUser();
        let url = this.systemConfiguration.SsoAuthorizationServer + Names.authorization.ssoLogoutPath;
        if (idToken) {
            url += `?id_token_hint=${idToken}&post_logout_redirect_uri=${encodeURIComponent(this.getLogoutSsoRedirectUri())}`;
        }
        this.globalService.window.location.replace(url);
    }

    storeNewState(): string {
        var state = Date.now() + "" + Math.random();
        this.globalService.localStorage.set(Names.authorization.stateParam, state);
        return state;
    }

    handleUnauthorized() {
        this.userService.clearUser();
        this.globalService.window.location.replace(this.systemConfiguration.SsoAuthorizationServer + Names.authorization.ssoUnauthorizedPath);
    }

    getStoredState(): string {
        return this.globalService.localStorage.get<string>(Names.authorization.stateParam);
    }

    resetStoredState(): void {
        this.globalService.localStorage.remove(Names.authorization.stateParam);
    }

    concludeSSOLogin() {
        // ConcludeSSOLogin should not be ran in silent login iframe
        // because it can change window location before we able to parse the result from silent login
        if (window.parent == window) {
            this.activatedRoute.queryParams.subscribe(params => {
                var authResponse: NPS.Common.Authorization.ISSOAuthResponse = (<any>params);
                this.verifyStateParameters(authResponse.state);
                this.saveUserInfo(authResponse.access_token);
                if (authResponse.id_token) {
                    this.localStorageService.set(ID_TOKEN_KEY, authResponse.id_token);
                }
                this.globalService.window.location.replace("/");
            });
        }
    }

    parseParams(path: string): any {
        var params: any = {};
        var regex = /([^#?/&=]+)=([^#?/&]*)/g;
        var match: RegExpExecArray;
        while (match = regex.exec(path)) {
            params[decodeURIComponent(match[1])] = decodeURIComponent(match[2]);
        }
        return params;
    }

    saveUserInfo(token: string): void {
        var claims = (<NPS.Common.Authorization.ITokenClaims>jwt_decode(token));
        if (this.userHasAccess(claims)) {
            var user:NPS.Common.IUser = {
                id: claims.sub,
                username: claims.preferred_username,
                firstName: claims.given_name,
                lastName: claims.family_name,
                email: claims.email,
                expires: moment.unix(claims.exp).utc(),
                companyId: claims.companyid,
                token: token,
                tenantId: claims.tenantid,
                availableTenants: claims.operator_tenantid,
                userType: this.getUserTypeEnumValue(claims.usertype)
            };
            this.userService.user = user;
        }
        this.checkRenewalTimer();
    }

    private getUserTypeEnumValue(userTypeStr: string): NPS.Common.UserType {
        switch (userTypeStr) {
            case "Web":
                return NPS.Common.UserType.Web;
            case "Api":
                return NPS.Common.UserType.Api;
            case "CompanyAdmin":
                return NPS.Common.UserType.SuperAdmin;
            case "Operator":
                return NPS.Common.UserType.Operator;
            default:
                return null;
        }    
    }

    verifyStateParameters(responseState: string): void {
        var stored = this.getStoredState();
        if (!stored || stored !== responseState) {
            this.locationService.go("/unauthorized");
        }
        this.resetStoredState();
    }

    userHasAccess(claims: NPS.Common.Authorization.ITokenClaims): boolean {
        var notAnOperator = !claims.operator_tenantid || !claims.operator_tenantid.length;
        var hasNoAppropirateSubscription = ("application_subscription" in claims)
                                        && claims.application_subscription.length
                                        && claims.application_subscription.indexOf(this.systemConfiguration.SsoClientId) === -1;

        if (notAnOperator || hasNoAppropirateSubscription) {
            this.handleUnauthorized();
            return false;
        } else {
            return true;
        }
    }
    
    checkRenewalTimer(): void {
        if(!this.expiredRenewalTimeout && this.userService.user) {
            this.addExpiredRenewal();
        }
    }

    addExpiredRenewal(): void {
        const msecond = 1000;
        const safeTime = 60000;
        const refreshTime = (moment(this.userService.user.expires).unix() - moment().unix()) * msecond - safeTime;
        this.expiredRenewalTimeout = window.setTimeout(() => {
            this.trySilentLogin().then(() => {
                this.addExpiredRenewal();
            }, () => {
                this.requestSSOLogin();
            });
        }, refreshTime);
    }
}


// WEBPACK FOOTER //
// ./~/@nordpoolgroup/nps-angular-2-common/src/Authorization/AuthorizationService.ts